// ═══════════════════════════════════════════════════════════════════
//  modules/projets/ProjectsList.js
//  ProjectsList — trois colonnes : À faire · En cours · Terminé
//
//  ── ETAT_PROJETS_V2 (06/09/2026) ────────────────────────────────────
//  Avant, un projet était « Actif » ou « Clôturé », et il fallait penser
//  à le clôturer. Personne n'y pensait : « Module Sanctions » affichait
//  Actif avec six tâches sur six terminées.
//
//  L'état n'est donc plus saisi, il est DÉDUIT des tâches — le back le
//  calcule et le renvoie dans `etat`. Un état que personne n'entretient
//  est un état qui finit par mentir, et on vient précisément de passer
//  six semaines à réparer ça.
//
//  Il reste un état explicite : « archivé » (status = cloture). Il couvre
//  DEUX cas qu'il ne faut pas confondre :
//    · un chantier arrêté sans être fini — une décision ;
//    · un projet VIDÉ, dont les tâches sont parties ailleurs — c'est le
//      cas des sept anciens projets thématiques, le 06/09.
//  Le compte de tâches de chaque carte tranche entre les deux, et le
//  libellé du repli ne prétend plus savoir lequel c'est. La première
//  version disait « arrêtés sans être finis » pour les deux : elle a
//  fait croire à des chantiers abandonnés là où il n'y avait que des
//  coquilles vides.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { ProjectView } from "./ProjectView";

const COLONNES = [
  { cle: "a_faire", titre: "À faire", couleur: THEME.gold, fond: THEME.goldDim,
    sous: "Ouvert, personne dessus pour l'instant.", vide: "Rien ici." },
  { cle: "en_cours", titre: "En cours", couleur: THEME.accent, fond: THEME.accentDim,
    sous: "Commencé. Au moins une tâche avance.", vide: "Rien ici." },
  { cle: "termine", titre: "Terminé", couleur: THEME.emerald, fond: THEME.emeraldDim,
    sous: "Toutes les tâches sont finies.", vide: "Rien ici." },
];

// Repli : un back pas encore à jour ne renvoie pas `etat`. Plutôt que
// d'afficher une colonne vide et de laisser croire que le tableau est
// vide, on recalcule ici la même chose.
function etatDe(p) {
  if (p.etat) return p.etat;
  if (p.status === "cloture") return "archive";
  if (!p.total_tasks) return "a_faire";
  if (p.done_tasks === p.total_tasks) return "termine";
  return p.done_tasks > 0 ? "en_cours" : "a_faire";
}

function Carte({ p, onOuvrir }) {
  return (
    <div onClick={onOuvrir}
      style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 16, cursor: "pointer" }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = THEME.gold)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = THEME.border)}>
      <div style={{ color: THEME.text, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{p.name}</div>
      {p.description && <div style={{ color: THEME.textMuted, fontSize: 12, marginBottom: 12 }}>{p.description}</div>}
      <div style={{ background: THEME.border, borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 6 }}>
        <div style={{ background: THEME.gold, width: `${p.progress}%`, height: "100%" }} />
      </div>
      <div style={{ color: THEME.textMuted, fontSize: 11 }}>
        {p.total_tasks ? `${p.progress}% — ${p.done_tasks}/${p.total_tasks} tâches` : "aucune tâche"}
      </div>
    </div>
  );
}

export function ProjectsList({ token, currentUserId }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [voirArchives, setVoirArchives] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const loadProjects = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setProjects(Array.isArray(data) ? data : []);
  }, [token]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const createProject = async () => {
    if (!newName.trim()) return;
    await fetch(`${API_BASE}/api/projects`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newName, description: newDesc }),
    });
    setNewName(""); setNewDesc(""); setShowNewProject(false); loadProjects();
  };

  if (selectedProject) return <ProjectView token={token} projectId={selectedProject} currentUserId={currentUserId} onBack={() => { setSelectedProject(null); loadProjects(); }} />;

  const archives = projects.filter((p) => etatDe(p) === "archive");
  const ouvrir = (id) => () => setSelectedProject(id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ color: THEME.text, fontSize: 18, fontWeight: 700, margin: 0 }}>📋 Projets</h2>
          <div style={{ color: THEME.textMuted, fontSize: 12, marginTop: 4 }}>
            L'état d'un projet se déduit de ses tâches — il n'y a rien à cocher.
          </div>
        </div>
        <button onClick={() => setShowNewProject(true)} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>+ Nouveau projet</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, alignItems: "start" }}>
        {COLONNES.map((c) => {
          const dedans = projects.filter((p) => etatDe(p) === c.cle);
          return (
            <div key={c.cle} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                <span style={{ color: c.couleur, fontWeight: 700, fontSize: 14 }}>{c.titre}</span>
                <Badge text={String(dedans.length)} bg={c.fond} color={c.couleur} />
              </div>
              <div style={{ color: THEME.textMuted, fontSize: 11, marginBottom: 12 }}>{c.sous}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {dedans.map((p) => <Carte key={p.id} p={p} onOuvrir={ouvrir(p.id)} />)}
                {dedans.length === 0 && <div style={{ color: THEME.textMuted, fontSize: 12, fontStyle: "italic" }}>{c.vide}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {projects.length === 0 && <div style={{ color: THEME.textMuted }}>Aucun projet pour l'instant.</div>}

      {archives.length > 0 && (
        <div>
          <button onClick={() => setVoirArchives((v) => !v)}
            style={{ background: "transparent", border: "none", color: THEME.textMuted, fontSize: 12, cursor: "pointer", padding: 0 }}>
            {voirArchives ? "▾" : "▸"} {archives.length} projet{archives.length > 1 ? "s" : ""} archivé{archives.length > 1 ? "s" : ""}
            {" "}— {archives.every((p) => !p.total_tasks)
              ? "vidés, ils ne contiennent plus rien"
              : "vidés ou arrêtés ; le compte de chaque carte dit ce qui reste dedans"}
          </button>
          {voirArchives && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14, marginTop: 12, opacity: 0.6 }}>
              {archives.map((p) => <Carte key={p.id} p={p} onOuvrir={ouvrir(p.id)} />)}
            </div>
          )}
        </div>
      )}

      {showNewProject && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={(e) => e.target === e.currentTarget && setShowNewProject(false)}>
          <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 14, padding: 28, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
            <h3 style={{ color: THEME.text, fontSize: 18, fontWeight: 700, margin: 0 }}>➕ Nouveau projet</h3>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du projet (ex: Le site, Le jeu...)"
              style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "10px 14px", color: THEME.text, fontSize: 14, outline: "none" }} />
            <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optionnel)" rows={3}
              style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "10px 14px", color: THEME.text, fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowNewProject(false)} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontSize: 13 }}>Annuler</button>
              <button onClick={createProject} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 8, padding: "9px 20px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
